import { toPercentage, toSignificant } from '@simplywallst/formatting';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';
import { useSelector } from 'react-redux';
import { getSummary, getAnalysis } from '@/pages/CompanyReport/redux/selectors';
import { getAveragesSelector } from '@ducks/averages/selectors';

interface Props {
  companyId: string;
}

const FutureGrowthRatesRawData = ({ companyId }: Props) => {
  const {
    uniqueSymbol,
    analystCount,
    industryData: { secondaryName: industrySecondaryName },
  } = useSelector((state) => getSummary(state, { companyId }));

  const {
    earningsGrowthAnnual: netIncomeGrowthAnnual,
    revenueGrowthAnnual,
    earningsPerShareGrowthAnnual,
  } = useSelector((state) => getAnalysis(state, { companyId }));

  const { getIndustryAverage, getMarketAverage } = useSelector((state) =>
    getAveragesSelector(state, { companyId })
  );
  const industryNetIncomeGrowthAnnual = getIndustryAverage(
    'netIncomeGrowthAnnual'
  );
  const industryAverageRevenueGrowthAnnual = getIndustryAverage(
    'revenueGrowthAnnual'
  ).value;
  const {
    source: industryCountryName,
    value: industryAverageNetIncomeGrowthAnnual,
  } = industryNetIncomeGrowthAnnual;
  const {
    source: marketCountryName,
    value: marketAverageNetIncomeGrowthAnnual,
  } = getMarketAverage('netIncomeGrowthAnnual');
  const marketAverageRevenueGrowthAnnual = getMarketAverage(
    'revenueGrowthAnnual'
  ).value;

  return (
    <>
      <DataTable columnWidths="4, 4, 2" light>
        <caption>{`${uniqueSymbol} Future Growth Rates Data Sources`}</caption>
        <thead>
          <tr>
            <th>Data Point</th>
            <th>Source</th>
            <th>Value (per year)</th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            if (netIncomeGrowthAnnual)
              return (
                <tr>
                  <td>{`${uniqueSymbol} Future Earnings Growth Rate`}</td>
                  <td>
                    <span>
                      Line of Best Fit<sup>*</sup>
                    </span>
                    <span>{`through Consensus Estimate Earnings of ${toSignificant(
                      analystCount,
                      0
                    )} Analysts `}</span>
                  </td>
                  <td>{toPercentage(netIncomeGrowthAnnual, 1)}</td>
                </tr>
              );
            else if (earningsPerShareGrowthAnnual)
              return (
                <tr>
                  <td>{`${uniqueSymbol} Future Earnings per Share Growth Rate`}</td>
                  <td>
                    <span>
                      Line of Best Fit<sup>*</sup>
                    </span>
                    <span>{` through Consensus Estimate Revenue of ${toSignificant(
                      analystCount,
                      0
                    )} Analysts `}</span>
                  </td>
                  <td>{toPercentage(earningsPerShareGrowthAnnual, 1)}</td>
                </tr>
              );
          })()}
          {revenueGrowthAnnual && (
            <tr>
              <td>{`${uniqueSymbol} Future Revenue Growth Rate`}</td>
              <td>
                <span>
                  Line of Best Fit<sup>*</sup>
                </span>
                <span>{` through Consensus Estimate Revenue of ${toSignificant(
                  analystCount,
                  0
                )} Analysts `}</span>
              </td>
              <td>{toPercentage(revenueGrowthAnnual, 1)}</td>
            </tr>
          )}
          <tr>
            <td>
              {`${industryCountryName} ${industrySecondaryName} Industry Earnings Growth Rate`}
            </td>
            <td>Market Cap Weighted Average</td>
            <td>{toPercentage(industryAverageNetIncomeGrowthAnnual, 1)}</td>
          </tr>
          <tr>
            <td>
              {`${industryCountryName} ${industrySecondaryName} Industry Revenue Growth Rate`}
            </td>
            <td>Market Cap Weighted Average</td>
            <td>{toPercentage(industryAverageRevenueGrowthAnnual, 1)}</td>
          </tr>
          <tr>
            <td>{`${marketCountryName} Market Earnings Growth Rate`}</td>
            <td>Market Cap Weighted Average</td>
            <td>{toPercentage(marketAverageNetIncomeGrowthAnnual, 1)}</td>
          </tr>
          <tr>
            <td>{`${marketCountryName} Market Revenue Growth Rate`}</td>
            <td>Market Cap Weighted Average</td>
            <td>{toPercentage(marketAverageRevenueGrowthAnnual, 1)}</td>
          </tr>
        </tbody>
      </DataTable>
      <p>
        <span>
          <sup>*</sup>
          {`Line of best fit is calculated by `}
        </span>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#future-performance`}
          data-unique-symbol={uniqueSymbol}
          data-about-section="future_growth_rates"
          data-link-name="model_linear_regression"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          linear regression
        </a>
        .
      </p>
      <p>
        <a
          href={`${SWS_ANALYSIS_MODEL}/industry_averages.markdown`}
          data-unique-symbol={uniqueSymbol}
          data-about-section="future_growth_rates"
          data-link-name="model_industry_averages"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Industry and Market average data
        </a>
        <span> is calculated daily.</span>
      </p>
    </>
  );
};

export default FutureGrowthRatesRawData;
